import type { FieldProp } from 'react-typed-form';

import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { formatDayInput, parseDayInput } from 'src/util';

import { ErrorList, Stack, Text } from '../components';

type Props = Readonly<{
  field: FieldProp<string | null>;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
}>;

/**
 * A day typed the way people say it — "15 Aug 2026" — held as the plain
 * yyyy-MM-dd the form stores.  What was typed is kept while the field has
 * focus, so a half-finished "15 Aug" is not reformatted out from under the
 * cursor.  The day is read and written without the timezone ever coming into
 * it, so a viewer's clock cannot shift the date.
 */
export default function FieldDate({
  field,
  label,
  placeholder,
  autoFocus,
}: Props) {
  const [typed, setTyped] = React.useState<string | null>(null);
  const isDisabled = field.isLoading;

  return (
    <Stack gap={0.5}>
      <Text
        typeface="body.small.regular"
        color={isDisabled ? 'neutral60' : 'neutral80'}
      >
        {label ?? field.label}
      </Text>
      <html.input
        style={[styles.input, isDisabled && styles.inputDisabled]}
        value={typed ?? formatDayInput(field.value ?? null) ?? ''}
        placeholder={placeholder ?? 'e.g. 15 Aug 2026'}
        autoFocus={autoFocus}
        disabled={isDisabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const text = e.target.value;
          setTyped(text);
          field.handleValueChange(
            text.trim() === '' ? null : parseDayInput(text)
          );
        }}
        onBlur={() => setTyped(null)}
      />
      <ErrorList errors={field.lastErrorList} />
    </Stack>
  );
}

const styles = css.create({
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
  inputDisabled: {
    backgroundColor: color.neutral15,
    color: color.neutral60,
    cursor: 'default',
  },
});
